<script setup lang="ts">
import { computed } from "vue";

const { t, tm } = useI18n();
const localePath = useLocalePath();

usePageSeo({
  title: () => t("pages.terms.meta.title"),
  description: () => t("pages.terms.meta.description"),
  keywords: () => t("pages.terms.meta.keywords"),
  robots: "noindex,follow",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "Terms", item: "/terms/" },
  ],
});

/* -----------------------------
   Structured content
----------------------------- */
type TermsSubsection = {
  subtitle: string;
  content?: string;
  list?: string[];
};

type TermsSection = {
  title: string;
  content?: string;
  list?: string[];
  note?: string;
  subsections?: TermsSubsection[];
};

const sections = computed(() => tm("pages.terms.sections") as TermsSection[]);
</script>

<template>
  <div class="max-w-5xl mx-auto px-6 py-10">
    <div class="prose prose-lg max-w-none">
      <!-- HERO -->
      <section class="text-center space-y-3">
        <h1 class="text-3xl font-bold">
          {{ t("pages.terms.hero.title") }}
        </h1>
        <p class="text-gray-600">
          {{ t("pages.terms.hero.subtitle") }}
        </p>
        <p class="text-sm text-gray-500">
          {{ t("pages.terms.hero.lastUpdated") }}
        </p>
      </section>

      <!-- INTRO -->
      <section class="mt-10 space-y-3">
        <h2 class="text-2xl font-semibold">
          {{ t("pages.terms.intro.title") }}
        </h2>
        <p>
          {{ t("pages.terms.intro.description") }}
        </p>
      </section>

      <!-- MAIN SECTIONS -->
      <section
        v-for="section in sections"
        :key="section.title"
        class="mt-12 space-y-4"
      >
        <h2 class="text-2xl font-semibold">
          {{ section.title }}
        </h2>

        <p v-if="section.content">
          {{ section.content }}
        </p>

        <ul v-if="section.list" class="list-disc pl-6 space-y-2">
          <li v-for="item in section.list" :key="item">
            {{ item }}
          </li>
        </ul>

        <p v-if="section.note" class="text-sm text-gray-600 italic">
          {{ section.note }}
        </p>

        <!-- Subsections -->
        <div v-if="section.subsections" class="space-y-6">
          <div
            v-for="sub in section.subsections"
            :key="sub.subtitle"
            class="space-y-2"
          >
            <h3 class="text-xl font-medium">
              {{ sub.subtitle }}
            </h3>

            <p v-if="sub.content">
              {{ sub.content }}
            </p>

            <ul v-if="sub.list" class="list-disc pl-6 space-y-2">
              <li v-for="item in sub.list" :key="item">
                {{ item }}
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- ACCEPTANCE -->
      <section class="mt-14 border-t pt-8 space-y-3">
        <h2 class="text-2xl font-semibold">
          {{ t("pages.terms.acceptance.title") }}
        </h2>
        <p>
          {{ t("pages.terms.acceptance.description") }}
        </p>
      </section>

      <!-- UPDATES -->
      <section class="mt-8 space-y-3">
        <h2 class="text-2xl font-semibold">
          {{ t("pages.terms.updates.title") }}
        </h2>
        <p>
          {{ t("pages.terms.updates.description") }}
        </p>
      </section>

      <!-- CTA -->
      <section class="text-center border-t mt-12 pt-10">
        <NuxtLink
          :to="localePath('/')"
          class="inline-block px-6 py-3 rounded-lg bg-black text-white font-medium"
        >
          {{ t("common.backToHome", "Back to Home") }}
        </NuxtLink>
      </section>
    </div>
  </div>
</template>
